import React, { useEffect, useMemo } from 'react';
import { Tag } from 'lucide-react';
import { renderContent } from './renderContent';
import { sanitizeHtml } from '../../lib/sanitize';
import { isPeptideCategory, isHyroxArticle } from './articleHelpers';
import { PeptiqInlineCTA, splitContentForCTA } from '../../components/PeptiqInlineCTA';
import { AuthorByline, MedicalDisclaimer, SourcesAndReferences, EvidenceOrganizations } from '../../components/articles/ArticleSEOComponents';
import { CentrInlineCTA } from '../../components/CentrAffiliate';
import { HyroxPredictorCTA } from '../../components/articles/HyroxPredictorCTA';
import { SeriesNavigation } from '../../components/articles/SeriesNavigation';
import { AIRecommendations } from '../../components/articles/AIRecommendations';
import { RelatedArticles } from '../../components/articles/RelatedArticles';
import { ArticleFeedbackWidget } from '../../components/articles/ArticleFeedbackWidget';
import { LeadMagnetMount } from '../../components/articles/LeadMagnet';
import { PeptideCalculator } from '../PeptideCalculatorPage';
import { ResearchDocGate } from '../../components/articles/ResearchDocGate';
import AprTrustBlock from '../../components/apr/AprTrustBlock';
import AprProductGrid from '../../components/apr/AprProductGrid';
import TenXProductCards from '../../components/Articles/TenXProductCards';
import ABTestCTA from '../../components/ABTestCTA';
import { ArticlePartnerOffers } from '../../components/partners/ArticlePartnerOffers';

const PeptidesContent = ({ article, contentRef }) => {
  // Memoized: React 19 re-sets dangerouslySetInnerHTML whenever the {__html}
  // object identity changes, wiping user input in inline forms on re-render.
  const { rawHtmlObj, firstObj, secondObj } = useMemo(() => {
    const rawHtml = article.content && article.content.trim().startsWith('<')
      ? article.content
      : renderContent(article.content);
    const [firstHalf, secondHalf] = splitContentForCTA(rawHtml);
    return {
      rawHtmlObj: { __html: rawHtml },
      firstObj: { __html: sanitizeHtml(firstHalf) },
      secondObj: secondHalf ? { __html: sanitizeHtml(secondHalf) } : null,
    };
  }, [article.content]);
  if (secondObj) {
    return (
      <>
        <div className="article-content" dangerouslySetInnerHTML={firstObj} />
        <PeptiqInlineCTA />
        <div ref={contentRef} className="article-content" dangerouslySetInnerHTML={secondObj} />
      </>
    );
  }
  return (
    <>
      <div ref={contentRef} className="article-content" dangerouslySetInnerHTML={rawHtmlObj} />
      <PeptiqInlineCTA />
    </>
  );
};

const StandardContent = ({ article, contentRef }) => {
  const htmlObj = useMemo(() => ({
    __html: sanitizeHtml(article.content && article.content.trim().startsWith('<')
      ? article.content
      : renderContent(article.content)),
  }), [article.content]);
  return (
    <div
      ref={contentRef}
      className="article-content"
      dangerouslySetInnerHTML={htmlObj}
    />
  );
};

export const ArticleBody = ({ article, slug, contentRef }) => {
  useEffect(() => {
    if (!article?.content?.includes('instagram-media')) return;
    const process = () => window.instgrm?.Embeds?.process();
    if (window.instgrm) { process(); return; }
    const s = document.createElement('script');
    s.src = 'https://www.instagram.com/embed.js';
    s.async = true;
    s.onload = process;
    document.body.appendChild(s);
  }, [article?.content]);

  return (
  <>
    <AuthorByline article={article} />
    <div className="lg:hidden"><ArticlePartnerOffers article={article} placement="mobile-intro" limit={1}/></div>
    {isPeptideCategory(article) && <MedicalDisclaimer />}

    {article.category === 'Peptides'
      ? <PeptidesContent article={article} contentRef={contentRef} />
      : <StandardContent article={article} contentRef={contentRef} />}

    <LeadMagnetMount article={article} />

    {slug === 'blood-tests-before-peptides-the-complete-pre-protocol-lab-guide' && <TenXProductCards />}
    {slug === 'recovery-amenities-gym-economics' && <ResearchDocGate />}

    {article.category === 'Peptides' && (
      <>
        <AprProductGrid matchText={`${article.title || ''} ${article.slug || ''} ${(article.tags || []).join(' ')}`} />
        <AprTrustBlock />
        <PeptideCalculator compact={true} />
      </>
    )}

    {isHyroxArticle(article) && (
      <>
        <HyroxPredictorCTA />
        <CentrInlineCTA />
      </>
    )}

    <ArticlePartnerOffers article={article} placement="article-end" />

    <div className="my-8" data-testid="ab-test-cta-container">
      <ABTestCTA articleSlug={article.slug} />
    </div>

    <EvidenceOrganizations sources={article.evidence_sources} />
    <SourcesAndReferences citations={article.citations} />

    <ArticleFeedbackWidget slug={article.slug} />

    {article.tags && article.tags.length > 0 && (
      <div className="mt-12 pt-8 border-t border-zinc-800">
        <div className="flex flex-wrap items-center gap-3">
          <Tag className="w-4 h-4 text-zinc-500" />
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 text-xs uppercase tracking-wider bg-zinc-900 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 transition-colors duration-300 cursor-pointer"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    )}

    <SeriesNavigation currentSlug={slug} />
    {isPeptideCategory(article) && <MedicalDisclaimer compact />}
    <AIRecommendations currentSlug={slug} category={article.category} />
    <RelatedArticles currentSlug={slug} category={article.category} internalLinks={article.internal_links} />
  </>
  );
};
